import {MNullable, toErrorMessage} from '../Toolkit/Utils';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import WithRouterProps from '../Toolkit/WithRouterProps';
import MPage, {MPageProps} from '../Toolkit/Components/Shell/Page';
import MComponent from '../Toolkit/Components/Component';
import EnumContext from '../Context/enum-context';
import {CreateRegistrationValidator, Registration, RegistrationReply} from '../Api/RegistrationTypes';
import React, {createRef, ReactElement, RefObject} from 'react';
import MAlertDialog from '../Toolkit/Components/Controls/AlertDialog';
import NSwagAdapter from '../Toolkit/Api/NSwagAdapter';
import {RegistrationClient, RegistrationCreateDto} from '../Api/RegistrationClients';
import MNSwagClientConfiguration from '../Toolkit/Api/NSwagClientConfiguration';
import SafeDigAccessTokenProvider from '../Api/SafeDigAccessTokenProvider';
import * as Yup from 'yup';
import {FieldProps, FormikHelpers} from 'formik';
import MForm, {MFormOutcome} from '../Toolkit/Components/Controls/Form';
import {MCloseIconName, MNavigateToIconName, MSaveIconName, MSuccessIcon} from '../Toolkit/Icons';
import MButton from '../Toolkit/Components/Controls/Button';
import MToolbar from '../Toolkit/Components/Controls/Toolbar';
import MSpacer from '../Toolkit/Components/Controls/Spacer';
import withRouter from '../Toolkit/WithRouter';
import AlertDialog from '../Toolkit/Components/Controls/AlertDialog';
import FormSection from '../Toolkit/Components/Controls/FormSection';
import MFormFieldControl from '../Toolkit/Components/Controls/FormFieldControl';
import MTextBox from '../Toolkit/Components/Controls/TextBox';
import MCalendar from '../Toolkit/Components/Controls/Calendar';
import MNumericBox from './Components/NumericBox';
import MDropDown from '../Toolkit/Components/Controls/DropDown';
import {MDropDownItem} from '../Toolkit/Components/Controls/DropDownOption';

class NewRegistration {
    public digLocation?: string = undefined;
    public reinstatementDate: MNullable<Date> = null;
    public digLength?: number = undefined;
    public digWidth?: number = undefined;
    public digDepth?: number = undefined;
    public eastings?: number = undefined;
    public northings?: number = undefined;
    public eastings2?: number = undefined;
    public northings2?: number = undefined;
    public tempPerm?: number = undefined;
    public surfaceType?: number = undefined;
}

type RouteParams = {
    noticeid: number;
};

type Props = WithMsalProps & WithRouterProps<RouteParams> & {};

type State = {
    page: MPageProps;
    pageTitle: string;
    pageSubTitle: string;
    form: {
        id: string;
        isSubmitting: boolean;
        registration: NewRegistration;
        validationSchema: any;
    }
}

class CreateRegistrationPage extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>

    private m_newRegistration: MNullable<Registration> | undefined;
    private readonly m_pageRef: RefObject<MPage>;
    private readonly m_dialogRef: RefObject<MAlertDialog>;
    private readonly m_registrationClient: NSwagAdapter<RegistrationClient>

    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_pageRef = createRef<MPage>();
        this.m_dialogRef = createRef<MAlertDialog>();
        this.m_registrationClient = new NSwagAdapter<RegistrationClient>(new RegistrationClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_SAFEDIGS_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));

        this.initializeState();
    }

    private bindMethods(): void {
        this.close_Click = this.close_Click.bind(this);
        this.na_click = this.na_click.bind(this);
        this.form_onBeforeSubmit = this.form_onBeforeSubmit.bind(this);
        this.form_onSubmit = this.form_onSubmit.bind(this);
        this.form_onAfterSubmit = this.form_onAfterSubmit.bind(this);
        this.form_onError = this.form_onError.bind(this);
        this.isFormDisabled = this.isFormDisabled.bind(this);
        this.showRegistrationCreatedDialog = this.showRegistrationCreatedDialog.bind(this);
    }

    private initializeState(): void {
        const page: MPageProps = {locked: false};

        const data: NewRegistration = {
            digLocation: undefined,
            reinstatementDate: null,
            digLength: undefined,
            digWidth: undefined,
            digDepth: undefined,
            eastings: undefined,
            northings: undefined,
            eastings2: undefined,
            northings2: undefined,
            tempPerm: -1,
            surfaceType: -1
        };

        const validationSchema: any = Yup.object().shape({
            surfaceType: CreateRegistrationValidator.surfaceType,
            reinstatementDate: CreateRegistrationValidator.reinstatementDate,
            digLocation: CreateRegistrationValidator.digLocation,
            digLength: CreateRegistrationValidator.digLength,
            digWidth: CreateRegistrationValidator.digWidth,
            digDepth: CreateRegistrationValidator.digDepth,
            tempPerm: CreateRegistrationValidator.tempPerm,
            eastings: CreateRegistrationValidator.eastings,
            northings: CreateRegistrationValidator.northings,
        });

        this.state = {
            page: page,
            pageTitle: 'Create New Registration',
            pageSubTitle: 'Create a new Registration entry',
            form: {
                isSubmitting: false,
                id: 'createRegistrationForm',
                registration: data,
                validationSchema: validationSchema
            }
        };
    }

    private isFormDisabled(): boolean {
        return this.state.form.isSubmitting;
    }

    private async close_Click(): Promise<void> {
        this.props.history.goBack();
    }

    private async na_click(): Promise<void> {
        const noticeId = this.props.match.params.noticeid;

        const newNotice = new RegistrationCreateDto({
            noticeId: noticeId,
            digLocation: 'Excavation no longer required.',
            reinstatementDate: new Date(),
            digLength: 0,
            digWidth: 0,
            digDepth: 0,
            eastings: 0,
            northings: 0,
            eastings2: undefined,
            northings2: undefined,
            tempPerm: true,
            surfaceType: -1
        });

        const result = await this.m_registrationClient.callWithReply(RegistrationReply, (client => client.create(newNotice)));

        result.throwIfFailed()

        this.props.history.goBack();
    }

    private async page_onLoadData(): Promise<void> {

    }

    private form_onBeforeSubmit(fieldValues: NewRegistration, formikHelpers: FormikHelpers<NewRegistration>): boolean {
        this.safeSetState(draft => draft.form.isSubmitting = true);
        return false;
    }

    private async form_onSubmit(fieldValues: NewRegistration, formikHelpers: FormikHelpers<NewRegistration>): Promise<void> {
        const noticeId = this.props.match.params.noticeid;

        const newNotice = new RegistrationCreateDto({
            noticeId: noticeId,
            digLocation: fieldValues.digLocation,
            reinstatementDate: fieldValues.reinstatementDate!,
            digLength: fieldValues.digLength,
            digWidth: fieldValues.digWidth,
            digDepth: fieldValues.digDepth,
            eastings: fieldValues.eastings,
            northings: fieldValues.northings,
            eastings2: fieldValues.eastings2,
            northings2: fieldValues.northings2,
            tempPerm: (fieldValues.tempPerm ?? 0) > 0,
            surfaceType: fieldValues.surfaceType
        });

        const result = await this.m_registrationClient.callWithReply(RegistrationReply, (client => client.create(newNotice)));

        result.throwIfFailed()

        this.m_newRegistration = result.reply!.registration;
    }

    private form_onAfterSubmit(outcome: MFormOutcome, fieldValues: NewRegistration, formikHelpers: FormikHelpers<NewRegistration>, exception: any | undefined): void {
        if(outcome === 'success') {
            this.showRegistrationCreatedDialog();
            return;
        }

        this.safeSetState(draft => draft.form.isSubmitting = false);
    }
    private form_onError(exception: any): boolean
    {
        const handled = false;
        this.m_pageRef.current!.messageBar.error(toErrorMessage(exception));
        return handled;
    }

    private showRegistrationCreatedDialog(): void {
        this.m_dialogRef.current!.show(
            'Create Registration',
            `A new registration has been created with ID ${this.m_newRegistration?.id}.`,
            MSuccessIcon,
            <MButton label="Open new registration"
                     color="success"
                     icon={MNavigateToIconName}
                     disabled={false}
                     onClick={() => this.props.history.replace(`/registrations/${this.m_newRegistration?.id}`)}/>);
    }

    private getToolbarTemplate(): ReactElement<MToolbar> {
        return (
            <MToolbar>
                <MButton label="Save"
                         formId={this.state.form.id}
                         type="submit"
                         color="success"
                         icon={MSaveIconName}
                         iconOnly={false}
                         isBusy={this.isFormDisabled}
                         disabled={this.isFormDisabled}/>
                <MSpacer/>
                <MButton label="Close"
                         icon={MCloseIconName}
                         disabled={this.isFormDisabled}
                         onClick={this.close_Click}/>
                <MSpacer/>
                <MButton label="Excavation N/A"
                         color="warning"
                         icon={'pi pi-thumbs-down'}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.na_click}/>
            </MToolbar>
        );

    }

    public render(): ReactElement {

        const tempPermList: MDropDownItem<number>[] = [{label: '-', value: -1}, {label: 'Temporary', value: 0},{label: 'Permanent', value: 1}];

        return (
            <MPage title={this.state.pageTitle}
                   subtitle={this.state.pageSubTitle}
                   locked={this.state.page.locked}
                   width={8}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}
                   ref={this.m_pageRef}>

                <AlertDialog ref={this.m_dialogRef}/>

                <MForm formId={this.state.form.id}
                       initialValues={this.state.form.registration}
                       validationSchema={this.state.form.validationSchema}
                       onBeforeSubmit={this.form_onBeforeSubmit}
                       onSubmit={this.form_onSubmit}
                       onAfterSubmit={this.form_onAfterSubmit}
                       onError={this.form_onError}>

                    <div className="formgrid grid">
                        <div className="col-12">

                            <FormSection title="General">
                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label={'Surface Type'}
                                                       fieldName={'surfaceType'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MDropDown<number> id={fieldProps.field.name}
                                                                   disabled={false}
                                                                   fieldProps={fieldProps}
                                                                   items={this.context?.enums === null ? [] : this.context?.enums.worksLocationTypes.map((enumvalue) => {
                                                                       return {label: enumvalue.name, value: enumvalue.id};
                                                                   })}
                                                />
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Reinstatement Date"
                                                       fieldName="reinstatementDate">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MCalendar id={fieldProps.field.name}
                                                           fieldProps={fieldProps}
                                                           disabled={this.isFormDisabled}
                                                           className={fieldClasses}/>
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-12">
                                    <MFormFieldControl label="Dig Location"
                                                       fieldName="digLocation">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          maxLength={Registration.DigLocationMaxLength}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isFormDisabled}
                                                          className={fieldClasses}
                                                          initialValue={fieldProps.field.value}
                                                />
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-3">
                                    <MFormFieldControl label="Dig Length (m)"
                                                       fieldName="digLength">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MNumericBox id={fieldProps.field.name}
                                                             maxLength={6}
                                                             fieldProps={fieldProps}
                                                             disabled={this.isFormDisabled}
                                                             className={fieldClasses}
                                                             decimalPlaces={2}
                                                             useGrouping={false}/>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-3">
                                    <MFormFieldControl label="Dig Width (m)"
                                                       fieldName="digWidth">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MNumericBox id={fieldProps.field.name}
                                                             maxLength={6}
                                                             fieldProps={fieldProps}
                                                             disabled={this.isFormDisabled}
                                                             className={fieldClasses}
                                                             decimalPlaces={2}
                                                             useGrouping={false}/>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-3">
                                    <MFormFieldControl label="Dig Depth (m)"
                                                       fieldName="digDepth">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MNumericBox id={fieldProps.field.name}
                                                             maxLength={6}
                                                             fieldProps={fieldProps}
                                                             disabled={this.isFormDisabled}
                                                             className={fieldClasses}
                                                             decimalPlaces={2}
                                                             useGrouping={false}/>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-3">
                                    <MFormFieldControl label={'Temp/Perm'}
                                                       fieldName={'tempPerm'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MDropDown<number> id={fieldProps.field.name}
                                                                    disabled={this.isFormDisabled}
                                                                    fieldProps={fieldProps}
                                                                    items={tempPermList}
                                                                    initialValue={-1}
                                                />
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                            </FormSection>

                            <FormSection title="Location">
                                <div className="col-12 md:col-6">
                                    <MFormFieldControl label="Start location - Eastings"
                                                       fieldName="eastings">
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MNumericBox id={fieldProps.field.name}
                                                             maxLength={8}
                                                             fieldProps={fieldProps}
                                                             disabled={this.isFormDisabled}
                                                             className={fieldClasses}
                                                             decimalPlaces={0}
                                                             useGrouping={false}/>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-6">
                                    <MFormFieldControl label="Start location - Northings"
                                                       fieldName="northings">
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MNumericBox id={fieldProps.field.name}
                                                             maxLength={8}
                                                             fieldProps={fieldProps}
                                                             disabled={this.isFormDisabled}
                                                             className={fieldClasses}
                                                             decimalPlaces={0}
                                                             useGrouping={false}/>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-6">
                                    <MFormFieldControl label="End location - Eastings"
                                                       fieldName="eastings2">
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MNumericBox id={fieldProps.field.name}
                                                             maxLength={8}
                                                             fieldProps={fieldProps}
                                                             disabled={this.isFormDisabled}
                                                             className={fieldClasses}
                                                             decimalPlaces={0}
                                                             useGrouping={false}/>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-6">
                                    <MFormFieldControl label="End location - Northings"
                                                       fieldName="northings2">
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MNumericBox id={fieldProps.field.name}
                                                             maxLength={8}
                                                             fieldProps={fieldProps}
                                                             disabled={this.isFormDisabled}
                                                             className={fieldClasses}
                                                             decimalPlaces={0}
                                                             useGrouping={false}/>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>
                            </FormSection>

                        </div>
                    </div>
                </MForm>
            </MPage>
        );
    }
}

export default withMsal(withRouter(CreateRegistrationPage));